"use client"

import { ChevronLeft, ChevronRight } from "@medusajs/icons"
import { useEffect, useMemo, useRef, useState } from "react"
import { PRODUCT_COPY } from "../../lib/product-copy"
import type { StoreProductMain } from "../../types"
import styles from "./compact-review-slider.module.css"

type Props = {
  slides: StoreProductMain["review_slides"]
  reviewSectionId: string
}

const Star = ({ filled }: { filled: boolean }) => (
  <svg className={filled ? styles.starFilled : styles.starEmpty} viewBox="0 0 24 24" role="img" aria-hidden="true" focusable="false">
    <path d="M12 2.25 14.97 8.2l6.53.95-4.73 4.61 1.12 6.52L12 17.98l-5.89 3.1 1.12-6.52-4.73-4.61 6.53-.95z" fill="currentColor" />
  </svg>
)

export default function CompactReviewSlider({ slides, reviewSectionId }: Props) {
  const ordered = useMemo(() => [...slides]
    .filter((slide) => slide.review_text?.trim())
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .slice(0, 3), [slides])
  const [index, setIndex] = useState(0)
  const [dragging, setDragging] = useState(false)
  const dragStart = useRef<number | null>(null)

  useEffect(() => {
    if (ordered.length <= 1) return
    const timer = window.setInterval(() => {
      if (!dragging) setIndex((current) => (current + 1) % ordered.length)
    }, 6000)
    return () => window.clearInterval(timer)
  }, [dragging, ordered.length])

  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(ordered.length - 1, 0)))
  }, [ordered.length])

  if (!ordered.length) return null

  const jumpToReviews = () => {
    const target = document.getElementById(reviewSectionId)
    if (!target) return
    target.scrollIntoView({ behavior: "smooth", block: "start" })
    target.focus({ preventScroll: true })
  }

  const pointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (typeof window !== "undefined" && window.innerWidth > 768) return
    if (event.pointerType === "mouse" && event.button !== 0) return
    dragStart.current = event.clientX
    setDragging(true)
  }
  const pointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStart.current === null) return
    const delta = event.clientX - dragStart.current
    dragStart.current = null
    setDragging(false)
    if (Math.abs(delta) < 50) return
    setIndex((current) => delta < 0 ? Math.min(ordered.length - 1, current + 1) : Math.max(0, current - 1))
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.slider} aria-label="Müşteri yorumları">
        <div className={styles.viewport} onPointerDown={pointerDown} onPointerUp={pointerUp} onPointerCancel={() => { dragStart.current = null; setDragging(false) }}>
          <div className={`${styles.track} ${dragging ? styles.trackDragging : ""}`} style={{ transform: `translateX(-${index * 100}%)` }}>
            {ordered.map((slide, slideIndex) => {
              const rating = Math.min(5, Math.max(0, Math.round(slide.rating ?? 5)))
              const avatar = slide.avatar_media?.type === "image" ? slide.avatar_media : null
              return (
                <div key={slide.id ?? `${slide.name}-${slideIndex}`} className={styles.slide} role="tabpanel" aria-label={slide.name} data-active={slideIndex === index ? "true" : "false"} aria-hidden={slideIndex === index ? undefined : true}>
                  <article className={styles.review}>
                    {avatar ? (
                      <button className={styles.avatarButton} type="button" onClick={jumpToReviews} aria-label="Yorumlar bölümüne git">
                        <img className={styles.avatar} src={avatar.url} alt={avatar.alt_text || `${slide.name} avatar`} loading="eager" />
                      </button>
                    ) : <span className={styles.avatarFallback} aria-hidden="true">{slide.name.trim().slice(0, 1).toLocaleUpperCase("tr-TR") || "V"}</span>}
                    <div className={styles.content}>
                      <p className={styles.reviewText}>{slide.review_text}</p>
                      <div className={styles.meta}>
                        <span className={styles.reviewerName}>{slide.name}</span>
                        <div className={styles.rating} role="img" aria-label={`${rating} out of 5 stars`}>
                          {Array.from({ length: 5 }, (_, starIndex) => <Star key={starIndex} filled={starIndex < rating} />)}
                        </div>
                      </div>
                    </div>
                  </article>
                </div>
              )
            })}
          </div>
        </div>
        {ordered.length > 1 ? (
          <div className={styles.navigation}>
            <button
              type="button"
              className={styles.navButton}
              aria-label="Önceki yorum"
              disabled={index === 0}
              onClick={() => setIndex((current) => Math.max(0, current - 1))}
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <div className={styles.navigationDots} role="tablist" aria-label="Yorum sayfaları">
              {ordered.map((slide, dotIndex) => (
                <button key={slide.id ?? dotIndex} className={`${styles.dot} ${dotIndex === index ? styles.dotActive : ""}`} type="button" role="tab" aria-label={`${dotIndex + 1}. yoruma git`} aria-selected={dotIndex === index} onClick={() => setIndex(dotIndex)} />
              ))}
            </div>
            <button
              type="button"
              className={styles.navButton}
              aria-label="Sonraki yorum"
              disabled={index === ordered.length - 1}
              onClick={() => setIndex((current) => Math.min(ordered.length - 1, current + 1))}
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
      <button type="button" className={styles.viewAllReviewsLink} onClick={jumpToReviews}>{PRODUCT_COPY.viewAllReviews}</button>
    </div>
  )
}
